import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Dividend } from "@rentablo/api/apiv1";
import { TableConfig } from "@rentablo/components";
import { toCurrency } from "@helpers";

export const useTableConfig = (): TableConfig<Dividend> => {
  const { t } = useTranslation();
  const { investmentId } = useParams() as { investmentId: string };
  const navigate = useNavigate();

  const editLink = (bookingId: string) => {
    navigate(`/investments/${investmentId}/dividend-wizard/edit/${bookingId}`);
  };

  const createLink = (path: string) => {
    navigate(
      `/investments/${investmentId}/dividend-wizard/create?date=${path}`
    );
  };

  const dividendsTableConfig: TableConfig<Dividend> = {
    id: "investmentDividends",
    getRowId: (row) => row.bookingId ?? row.date ?? "",
    columns: [
      {
        id: "date",
        title: t("domains.dividends.dividendsTable.headers.date"),
        align: "left",
        renderer: ({ row }) => (
          <Box display="flex">
            <Typography
              sx={{
                fontWeight: "bold",
                textTransform: "none",
                cursor: "pointer",
              }}
            >
              {row.date ? format(new Date(row.date), "dd.MM.yyyy") : ""}
            </Typography>
          </Box>
        ),
      },
      {
        id: "dividendPerShare",
        title: t("domains.dividends.dividendsTable.headers.dividend"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body1">
            {toCurrency(row.dividendPerShare, row.currency)}
          </Typography>
        ),
      },
      {
        id: "numberOfLots",
        title: t("domains.dividends.dividendsTable.headers.lots"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body1">{row.numberOfLots ?? 0}</Typography>
        ),
      },
      {
        id: "grossAmount",
        title: t("domains.dividends.dividendsTable.headers.total"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body1">
            {toCurrency(row.grossAmount, row.currency)}
          </Typography>
        ),
      },
      {
        id: "actions",
        title: t("domains.dividends.dividendsTable.headers.actions"),
        align: "right",
        renderer: ({ row }) => {
          const hasBooking = Boolean(row.bookingId);
          const disabled = row.numberOfLots === 0;

          if (hasBooking) {
            return (
              <Button
                variant="text"
                color="primary"
                disabled={disabled}
                onClick={() =>
                  row.bookingId ? editLink(row.bookingId.toString()) : null
                }
              >
                {t("domains.dividends.dividendsTable.actions.editDividends")}
              </Button>
            );
          }

          return (
            <Button
              variant="text"
              color="primary"
              disabled={disabled}
              onClick={() =>
                row.date
                  ? createLink(format(new Date(row.date), "MM-dd-yyyy"))
                  : null
              }
            >
              {t("domains.dividends.dividendsTable.actions.addDividends")}
            </Button>
          );
        },
      },
    ],
  };

  return dividendsTableConfig;
};
